// Status label (Business UI handoff): a small 4px-radius badge whose colour
// always backs up the WORD — colour alone never carries meaning.
//
// active            → ok (green text on green tint)
// prospect          → neutral (in progress)
// dormant           → warn (needs attention)
// closed / lost     → muted (archived)
// warn              → warn (a flag, e.g. credit hold)
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

// Single source of truth for tone → brand-class. Exported so other brand
// surfaces (e.g. the project stage rail's node dots) COMPOSE these exact values
// instead of re-declaring near-identical hex tints that drift out of sync.
export const STATUS_PILL_TONE_CLASS = {
  active: "bg-ok-bg text-ok",
  prospect: "bg-[#f1f1ee] text-ink",
  dormant: "bg-warn-bg text-warn",
  closed: "bg-[#f1f1ee] text-muted",
  warn: "bg-warn-bg text-warn",
  info: "bg-[#f1f1ee] text-ink",
} as const;

const pillVariants = cva(
  "inline-flex items-center whitespace-nowrap rounded px-2 py-1 text-[11px] font-medium capitalize",
  {
    variants: {
      tone: STATUS_PILL_TONE_CLASS,
    },
    defaultVariants: { tone: "active" },
  },
);

interface StatusPillProps extends VariantProps<typeof pillVariants> {
  children: React.ReactNode;
  className?: string;
}

export function StatusPill({
  tone,
  children,
  className,
}: StatusPillProps) {
  return (
    <span className={cn(pillVariants({ tone }), className)}>{children}</span>
  );
}

/** Map a backend `client_status` enum value to the right pill tone. */
export function clientStatusTone(
  status: string | null | undefined,
): "active" | "prospect" | "dormant" | "closed" | "info" {
  switch (status) {
    case "active":
      return "active";
    case "prospect":
      return "prospect";
    case "dormant":
      return "dormant";
    case "closed":
    case "on_credit_hold":
      return "closed";
    default:
      return "info";
  }
}
